import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Flag,
  Grid3x3,
  Pause,
  Timer,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { sendOnUnload } from '@/api/client';
import { ApiError, mensajeDe } from '@/api/errors';
import { LogoCaso } from '@/components/MarcaCaso';
import { CajaRotulo } from '@/components/BloquesConsalud';
import { ImagenProtegida } from '@/components/ContenidoProtegido';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { answerFeedback, pressFeedback, swapCase } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useAtribucionAlCopiar } from '@/lib/atribucion';

import { Cargando, ErrorCarga } from '../curso/componentes';

import { reloj } from './comun';
import {
  cambiarEstado,
  confirmarCaso,
  guardarRespuestas,
  RUTAS,
  useIntento,
  type Attempt,
  type AttemptCase,
  type Guardado,
  type Letra,
  type Tipo,
} from './queries';
import { ReportarPregunta } from './ReportarPregunta';

const clave = (ci: number, qid: number) => `${String(ci)}:${String(qid)}`;

/** Resolver un intento (simulador o examen). La API guarda y califica; aquí solo se elige. */
export function EvaluacionPage({ tipo }: { tipo: Tipo }) {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const q = useIntento(tipo, id);
  if (!Number.isInteger(id) || id <= 0) return <Navigate to={RUTAS[tipo].app} replace />;
  if (q.isPending) return <Cargando alto="h-96" />;
  if (q.error) return <ErrorCarga error={q.error} />;
  if (q.data.status === 'finished') {
    return <Navigate to={`${RUTAS[tipo].app}/${String(id)}/resultados`} replace />;
  }
  return <Resolver tipo={tipo} intento={q.data} />;
}

function Resolver({ tipo, intento }: { tipo: Tipo; intento: Attempt }) {
  useAtribucionAlCopiar();
  // Misma consulta que el padre (caché compartida): renueva las URLs firmadas si una venció.
  const { refetch } = useIntento(tipo, intento.id);
  const navigate = useNavigate();
  const salir = tipo === 'simulator' ? '/simulador' : '/curso-enarm/examenes-prime';
  const total = intento.cases.length;

  // Estado local: respuestas, tiempos y casos confirmados. La consulta se refresca cada 50 s
  // solo para renovar las URLs firmadas de las imágenes; lo contestado manda desde aquí.
  const [resp, setResp] = useState<Record<string, Letra | null>>(() =>
    Object.fromEntries(
      intento.cases.flatMap((c) => c.questions.map((p) => [clave(c.index, p.id), p.selected])),
    ),
  );
  const [segundos, setSegundos] = useState<Record<number, number>>(() =>
    Object.fromEntries(intento.cases.map((c) => [c.index, c.seconds])),
  );
  const [revelados, setRevelados] = useState<Record<number, AttemptCase>>(() =>
    Object.fromEntries(intento.cases.filter((c) => c.confirmed).map((c) => [c.index, c])),
  );
  const [actual, setActual] = useState(() => {
    const i = intento.cases.findIndex((c) => !c.complete);
    return i >= 0 ? i : total - 1;
  });
  const [progreso, setProgreso] = useState(false);
  const [finalizar, setFinalizar] = useState(false);
  const [reporte, setReporte] = useState<number | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const tarjeta = useRef<HTMLDivElement>(null);

  // ------------------------------------------------------------------ autosave
  const pendiente = useRef<Guardado>({ answers: [], times: [] });
  const tiemposSucios = useRef(new Set<number>());
  const segundosRef = useRef(segundos);
  useLayoutEffect(() => {
    segundosRef.current = segundos;
  });
  const temporizador = useRef<number | undefined>(undefined);

  const armarLote = useCallback((): Guardado | null => {
    const lote: Guardado = {
      answers: pendiente.current.answers,
      times: [...tiemposSucios.current].map((ci) => ({
        caseIndex: ci,
        seconds: segundosRef.current[ci] ?? 0,
      })),
    };
    pendiente.current = { answers: [], times: [] };
    tiemposSucios.current.clear();
    return lote.answers.length || lote.times.length ? lote : null;
  }, []);

  const guardar = useCallback(async () => {
    window.clearTimeout(temporizador.current);
    const lote = armarLote();
    if (!lote) return;
    try {
      await guardarRespuestas(tipo, intento.id, lote);
    } catch (e) {
      // Se reintenta en el siguiente guardado (las respuestas son idempotentes).
      pendiente.current.answers.unshift(...lote.answers);
      lote.times.forEach((t) => tiemposSucios.current.add(t.caseIndex));
      if (e instanceof ApiError && (e.code === 'ATTEMPT_FINISHED' || e.code === 'ANSWER_LOCKED')) {
        toast.error(e.message);
      }
      throw e;
    }
  }, [armarLote, tipo, intento.id]);

  const programar = useCallback(() => {
    window.clearTimeout(temporizador.current);
    temporizador.current = window.setTimeout(() => {
      void guardar().catch(() => undefined);
    }, 500);
  }, [guardar]);

  // Al cerrar o esconder la pestaña: envío con keepalive (sobrevive al cierre).
  useEffect(() => {
    const alSalir = () => {
      const lote = armarLote();
      if (lote) sendOnUnload(`${RUTAS[tipo].api}/${String(intento.id)}/answers`, 'PUT', lote);
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') alSalir();
    };
    window.addEventListener('pagehide', alSalir);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', alSalir);
      document.removeEventListener('visibilitychange', onVis);
      alSalir();
    };
  }, [armarLote, tipo, intento.id]);

  // Cronómetro del caso (corre solo con la pestaña visible).
  useEffect(() => {
    const t = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      setSegundos((s) => ({ ...s, [actual]: (s[actual] ?? 0) + 1 }));
      tiemposSucios.current.add(actual);
    }, 1000);
    return () => {
      window.clearInterval(t);
    };
  }, [actual]);

  const caso = intento.cases[actual];
  if (!caso) return <ErrorCarga error={new Error('No encontramos este caso.')} />;
  const revelado = revelados[actual];
  const completo = (c: AttemptCase) => c.questions.every((p) => resp[clave(c.index, p.id)]);
  const contestadas = intento.cases.reduce(
    (n, c) => n + c.questions.filter((p) => resp[clave(c.index, p.id)]).length,
    0,
  );
  const preguntas = intento.cases.reduce((n, c) => n + c.questions.length, 0);
  const entrenamiento = intento.mode === 'entrenamiento';

  const elegir = (qid: number, letra: Letra) => {
    if (revelado) return;
    setResp((r) => ({ ...r, [clave(actual, qid)]: letra }));
    pendiente.current.answers = pendiente.current.answers
      .filter((a) => !(a.caseIndex === actual && a.questionId === qid))
      .concat({ caseIndex: actual, questionId: qid, option: letra });
    tiemposSucios.current.add(actual);
    programar();
  };

  const ir = (n: number) => {
    if (n < 0 || n >= total) return;
    tiemposSucios.current.add(actual);
    void guardar().catch(() => undefined);
    swapCase(null, tarjeta.current, n > actual ? 1 : -1);
    setActual(n);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmar = async () => {
    setOcupado(true);
    try {
      await guardar();
      const c = await confirmarCaso(tipo, intento.id, actual);
      setRevelados((r) => ({ ...r, [actual]: c }));
      requestAnimationFrame(() => {
        c.questions.forEach((p) => {
          const el = tarjeta.current?.querySelector<HTMLElement>(
            `[data-q="${String(p.id)}"] [data-opcion="${p.selected ?? ''}"]`,
          );
          answerFeedback(el ?? null, { correct: !!p.isCorrect });
        });
      });
    } catch (e) {
      toast.error(mensajeDe(e));
    } finally {
      setOcupado(false);
    }
  };

  const cambiar = async (accion: 'pause' | 'finish') => {
    setOcupado(true);
    try {
      tiemposSucios.current.add(actual);
      await guardar();
      await cambiarEstado(tipo, intento.id, accion);
      void navigate(
        accion === 'finish' ? `${RUTAS[tipo].app}/${String(intento.id)}/resultados` : salir,
      );
      if (accion === 'pause')
        toast.success('Guardamos tu avance. Puedes reanudarlo cuando quieras.');
    } catch (e) {
      toast.error(mensajeDe(e));
      setOcupado(false);
    }
  };

  const ultimo = actual === total - 1;
  const seg = segundos[actual] ?? 0;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <header className="panel-vidrio flex flex-wrap items-center gap-3 !py-3">
        <p className="font-extrabold">
          <span className="text-azul-texto uppercase">
            {tipo === 'simulator' ? 'Simulador' : 'Examen'}
          </span>{' '}
          · Caso <span className="text-cian-texto tabular">{actual + 1}</span> de {total}
        </p>
        <span className="insignia">{entrenamiento ? 'Entrenamiento' : 'Modo ENARM'}</span>
        <span
          className={cn(
            'ml-auto inline-flex items-center gap-1 font-bold tabular',
            seg > 60 ? 'text-rojo-texto' : 'text-texto-2',
          )}
          aria-label="Tiempo en este caso"
        >
          <Timer size={16} />
          <span className="hidden text-xs font-extrabold text-azul-texto sm:inline">
            TIEMPO
          </span>{' '}
          {reloj(seg)}
        </span>
        <button
          aria-label="Progreso"
          type="button"
          className="btn-cs btn-vidrio"
          onClick={() => setProgreso(true)}
        >
          <Grid3x3 size={16} /> <span className="hidden sm:inline">Ver progreso</span>
        </button>
        <button
          aria-label="Pausar"
          type="button"
          className="btn-cs btn-vidrio"
          disabled={ocupado}
          onClick={() => void cambiar('pause')}
        >
          <Pause size={16} /> <span className="hidden sm:inline">Pausar</span>
        </button>
      </header>
      <div className="h-1.5 overflow-hidden rounded-full bg-verde-500/25" aria-hidden>
        <div
          className="h-full bg-cian-500 transition-[width] duration-500"
          style={{ width: `${String((contestadas * 100) / Math.max(preguntas, 1))}%` }}
        />
      </div>

      <article ref={tarjeta} className="panel-vidrio space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="pill-marca">
            {caso.specialty}
            {caso.subspecialty && caso.subspecialty !== caso.specialty
              ? ` · ${caso.subspecialty}`
              : ''}
          </span>
          {caso.english && <span className="insignia">Inglés</span>}
          <button
            type="button"
            className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-texto-3 hover:text-texto"
            onClick={() => setReporte(caso.questions[0]?.id ?? null)}
          >
            <Flag size={14} /> Creo que este caso necesita revisión
          </button>
        </div>
        {caso.vignette && (
          <div className="relative pt-3.5">
            {/* Pestaña «CASO CLÍNICO» del legacy, montada sobre el borde del caso */}
            <p className="absolute top-0 left-1/2 z-10 -translate-x-1/2 rounded-md bg-marca px-6 py-1 text-xs whitespace-nowrap text-white sm:text-sm">
              CASO CLÍNICO –{' '}
              <b className="tabular">
                {actual + 1} de {total}
              </b>
            </p>
            <p className="superficie-lectura border-verde-500/70 p-5 pt-7 leading-relaxed whitespace-pre-line">
              <LogoCaso className="mr-2 align-[-2px]" />
              {caso.vignette}
            </p>
          </div>
        )}
        {caso.imageUrl && (
          <ImagenProtegida
            src={caso.imageUrl}
            clave={`${String(intento.id)}-${String(actual)}`}
            onExpirada={() => refetch()}
            alt="Imagen del caso clínico"
            className="mx-auto max-h-96 w-auto rounded-xl"
          />
        )}
        {caso.questions.map((p, qi) => {
          const sel = resp[clave(actual, p.id)] ?? null;
          const r = revelado?.questions.find((x) => x.id === p.id);
          return (
            <section key={p.id} data-q={p.id} className="space-y-3">
              <p className="barra-pregunta normal-case">
                <LogoCaso variante="blanco" className="shrink-0" />
                {caso.questions.length > 1 && <span className="text-cian-300">{qi + 1}.</span>}{' '}
                {p.text}
              </p>
              <div role="radiogroup" className="space-y-2">
                {p.options.map((o) => {
                  const estado = r
                    ? o.key === r.correctOption
                      ? 'correcta'
                      : o.key === sel
                        ? 'incorrecta'
                        : undefined
                    : sel === o.key
                      ? 'seleccionada'
                      : undefined;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      role="radio"
                      aria-checked={sel === o.key}
                      data-opcion={o.key}
                      data-estado={estado}
                      disabled={!!r || ocupado}
                      className="opcion w-full text-left disabled:cursor-default"
                      onClick={(e) => {
                        pressFeedback(e.currentTarget);
                        elegir(p.id, o.key);
                      }}
                    >
                      <span className="opcion__letra flex flex-col gap-0.5">
                        {o.key}
                        <LogoCaso variante="blanco" className="h-2" />
                      </span>
                      <span className="opcion__texto normal-case">{o.text}</span>
                    </button>
                  );
                })}
              </div>
              {r && (
                <>
                  <CajaRotulo rotulo="Respuesta">
                    <p
                      className={cn(
                        'flex items-center gap-2 font-extrabold',
                        r.isCorrect ? 'text-verde-texto' : 'text-rojo-texto',
                      )}
                    >
                      {r.isCorrect ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
                      {r.isCorrect
                        ? '¡Correcto!'
                        : `Incorrecto: la respuesta correcta es la ${r.correctOption ?? ''}`}
                    </p>
                  </CajaRotulo>
                  <CajaRotulo rotulo="Justificación">
                    <p className="text-sm leading-relaxed whitespace-pre-line">{r.justification}</p>
                  </CajaRotulo>
                </>
              )}
            </section>
          );
        })}
      </article>

      <nav className="flex items-center justify-between gap-2" aria-label="Navegación de casos">
        <button
          type="button"
          className="btn-cs btn-vidrio"
          disabled={actual === 0 || ocupado}
          onClick={() => ir(actual - 1)}
        >
          <ChevronLeft size={16} /> Anterior
        </button>
        <div className="flex gap-2">
          {entrenamiento && !revelado && completo(caso) && (
            <button
              type="button"
              className="btn-cs btn-exito"
              disabled={ocupado}
              onClick={() => void confirmar()}
            >
              Confirmar mi respuesta
            </button>
          )}
          {ultimo ? (
            <button
              type="button"
              className="btn-cs btn-primario"
              disabled={ocupado || (entrenamiento && completo(caso) && !revelado)}
              onClick={() => {
                void guardar().catch(() => undefined);
                setFinalizar(true);
              }}
            >
              Finalizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-cs btn-primario"
              disabled={ocupado || (entrenamiento && completo(caso) && !revelado)}
              onClick={() => ir(actual + 1)}
            >
              {completo(caso) ? 'Siguiente' : 'Brincar caso'} <ChevronRight size={16} />
            </button>
          )}
        </div>
      </nav>

      <Dialog open={progreso} onOpenChange={setProgreso}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogTitle className="text-azul-texto">Seguimiento de los casos clínicos</DialogTitle>
          <DialogDescription>
            {contestadas} de {preguntas} preguntas contestadas. Toca un caso para ir a él.
          </DialogDescription>
          <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-texto-2">
            Nomenclatura:
            <span className="rounded-md border-2 border-amarillo-400 px-2 py-0.5">
              Caso incompleto
            </span>
            <span className="rounded-md border-2 border-exito bg-exito px-2 py-0.5 text-white">
              Caso completo
            </span>
          </p>
          <div className="mt-4 grid grid-cols-5 gap-2 sm:grid-cols-8">
            {intento.cases.map((c) => (
              <button
                key={c.index}
                type="button"
                onClick={() => {
                  setProgreso(false);
                  ir(c.index);
                }}
                aria-current={c.index === actual ? 'true' : undefined}
                className={cn(
                  'rounded-lg border p-2 text-sm font-extrabold tabular transition-colors',
                  'border-2',
                  completo(c)
                    ? 'border-exito bg-exito text-white'
                    : 'border-amarillo-400 hover:bg-amarillo-400/15',
                  c.index === actual && 'ring-2 ring-azul-600',
                )}
              >
                {c.index + 1}
                <span className="mt-1 flex justify-center gap-0.5">
                  {c.questions.map((p) => (
                    <span
                      key={p.id}
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        resp[clave(c.index, p.id)]
                          ? completo(c)
                            ? 'bg-white'
                            : 'bg-verde-500'
                          : 'bg-texto-3/40',
                      )}
                    />
                  ))}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={finalizar} onOpenChange={setFinalizar}>
        <DialogContent>
          <DialogTitle>
            ¿Terminar {tipo === 'simulator' ? 'el simulador' : 'el examen'}?
          </DialogTitle>
          <DialogDescription>
            Contestaste {contestadas} de {preguntas} preguntas en {total} casos.
            {contestadas < preguntas && ' Las preguntas sin contestar cuentan como incorrectas.'}
          </DialogDescription>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" className="btn-cs btn-vidrio" onClick={() => setFinalizar(false)}>
              Seguir resolviendo
            </button>
            <button
              type="button"
              className="btn-cs btn-exito"
              disabled={ocupado}
              onClick={() => void cambiar('finish')}
            >
              Sí, terminar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <ReportarPregunta questionId={reporte} onClose={() => setReporte(null)} />
    </div>
  );
}
